import { buildWhatsAppLink } from "@/lib/whatsapp";

/**
 * Editorial typographic logomark for "do Corte ao Lucro*".
 * Follows the official 2026 brand guide:
 *  - "do" minúsculo
 *  - serif elegante (Cormorant Garamond)
 *  - "Lucro" em itálico
 *  - asterisco amarelo (#F2C12E) como chancela sobrescrita
 *  - sem número 2 pesado — apenas eyebrow textual
 */
const Wordmark = ({ stacked = true, size = "xl" }) => {
  const baseSize =
    size === "lg"
      ? "text-5xl md:text-6xl lg:text-7xl"
      : size === "md"
        ? "text-4xl md:text-5xl"
        : "text-[3.5rem] sm:text-7xl md:text-[6.5rem] lg:text-[8rem]";

  return (
    <p
      className={`font-serif-mark leading-[0.95] tracking-[-0.02em] text-[#0d0d0d] ${baseSize}`}
      aria-label="do Corte ao Lucro"
    >
      {stacked ? (
        <>
          <span className="block">do Corte</span>
          <span className="block">
            ao <em className="italic">Lucro</em>
            <span
              aria-hidden
              className="text-[#f2c12e] align-super text-[0.5em] ml-0.5 inline-block translate-y-[0.15em]"
            >
              ✱
            </span>
          </span>
        </>
      ) : (
        <span className="whitespace-nowrap">
          do Corte ao <em className="italic">Lucro</em>
          <span
            aria-hidden
            className="text-[#f2c12e] align-super text-[0.5em] ml-0.5 inline-block translate-y-[0.15em]"
          >
            ✱
          </span>
        </span>
      )}
    </p>
  );
};

export const DoCorteAoLucro = () => {
  return (
    <section
      id="do-corte-ao-lucro"
      data-testid="docorte-section"
      className="bg-[#fafaf7] py-28 md:py-40 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Top meta line — Osklen-grade quiet header */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-6 mb-16 md:mb-24 reveal">
          <div className="md:col-span-6 flex items-center gap-4">
            <span className="rule-yellow" />
            <span className="eyebrow">Treinamento estratégico</span>
          </div>
          <div className="md:col-span-6 flex md:justify-end">
            <span className="pill-chip is-active" data-testid="docorte-edition-pill">
              Edição 2026 · Segunda fase
            </span>
          </div>
        </div>

        {/* Typographic logomark — centerpiece */}
        <div className="reveal" data-testid="docorte-wordmark">
          <Wordmark stacked />
          <div className="mt-8 md:mt-10 flex items-center gap-6">
            <span className="block w-20 md:w-28 h-[3px] bg-[#f2c12e]" />
            <span className="hidden md:block eyebrow text-[#6b6b6b]">
              Nova fase · Identidade 2026
            </span>
          </div>
          <p
            className="mt-10 md:mt-12 font-sans-oasys text-xl md:text-2xl lg:text-[1.6rem] leading-[1.35] text-[#2a2a2a] max-w-3xl"
            data-testid="docorte-tagline"
          >
            Como transformar atendimentos em inteligência de crescimento.
          </p>
        </div>

        {/* Editorial body grid */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 reveal">
          <div className="lg:col-span-7 space-y-6 text-[1.05rem] md:text-lg leading-[1.7] text-[#2a2a2a]">
            <p>
              Todo salão já produz informação: no agendamento, no retorno,
              na ausência, no ticket, na frequência, no serviço escolhido,
              na cliente que volta e na cliente que desaparece.
            </p>
            <p>
              O problema é que muitos profissionais ainda chamam isso
              apenas de rotina.
            </p>
            <p>
              O <em className="italic text-[#0d0d0d]">Do Corte ao Lucro</em>{" "}
              ensina o profissional da beleza a ler os sinais que o próprio
              salão revela todos os dias.
            </p>
            <p>
              Porque crescimento não nasce apenas do movimento. Nasce da
              capacidade de transformar atendimento em leitura, leitura em
              decisão e decisão em direção.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="border-t border-[#0d0d0d] pt-6">
              <p className="eyebrow text-[#6b6b6b] mb-6">
                Quatro lentes de leitura
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                {[
                  { k: "Leitura", v: "de agenda" },
                  { k: "Ticket", v: "e frequência" },
                  { k: "Retorno", v: "e ausência" },
                  { k: "Decisão", v: "com dado" },
                ].map((b) => (
                  <div key={b.k} className="flex items-baseline gap-3">
                    <span
                      aria-hidden
                      className="font-serif-mark text-[#f2c12e] text-lg leading-none"
                    >
                      ✱
                    </span>
                    <div>
                      <p className="font-display text-base md:text-lg text-[#0d0d0d] font-semibold">
                        {b.k}
                      </p>
                      <p className="text-[13px] text-[#6b6b6b] mt-0.5">
                        {b.v}
                      </p>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 md:mt-24 flex flex-wrap items-center gap-6 reveal">
          <a
            href={buildWhatsAppLink(
              "Olá! Quero conhecer o treinamento Do Corte ao Lucro — Edição 2026.",
            )}
            target="_blank"
            rel="noreferrer"
            data-testid="docorte-cta"
            className="btn-primary"
          >
            Conhecer o treinamento
            <span aria-hidden>→</span>
          </a>
          <a
            href="#formacoes"
            data-testid="docorte-cta-secondary"
            className="text-link-arrow"
          >
            Ver todas as formações
            <span aria-hidden className="arrow">
              →
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};
